import {Component, ElementRef, Inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {DOCUMENT, Location} from "@angular/common";
import {ActivatedRoute, Router} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {Subscription, timer} from 'rxjs';
import {finalize, delay} from 'rxjs/operators';

import {JASO} from '../shared/defines/gukeo.const';
import {
  CONTAINER_SCROLL_DELAY,
  dialectSelectId,
  SearchParam,
  triggerDetailPopup,
  wordSelectId,
  DEFAULT_SCROLL_SPEED,
  WORD_DETAIL_HEIGHT_DELTA_LARGE,
  WORD_DETAIL_HEIGHT_DELTA,
  DIALECT_DETAIL_HEIGHT_DELTA,
  proverbSelectedId,
  saseungSelectId,
  LIST_TYPE,
  DIALECT_TOTAL_HEIGHT_DELTA,
  HISTORY_KEY_SEARCH_TOTAL,
  DETAIL_POPUP_DELAY,
  EXAMPLE_DETAIL_HEIGHT_DELTA,
} from '../shared/defines/client.const';
import {IsEqualObj1} from '../shared/defines/common.func';
import {consoleEl, getDetailHeight, GetSearchFromQuery, scrollToNode} from '../shared/defines/client.func';
import {NormalizeSearchParamKeyword} from '../shared/defines/search-keyword.func';
import {SimpleWord} from '../shared/models/simple-word.model';
import {Word} from "../shared/models/word.model";
import {DialectMapPayload, SimpleDialect} from '../shared/models/dialect.model';
import {SimpleProverb} from '../shared/models/proverb.model';
import {Video} from '../shared/models/video.model';
import {Saseung3} from '../shared/models/saseung3.model';
import {Example} from '../shared/models/example.model';
import {ExampleListItemComponent} from '../example/example-list-item/example-list-item.component';
import {LayoutMgrService} from '../shared/services/layout-mgr/layout-mgr.service';
import {ResizeService} from '../shared/services/resize/resize.service';
import {VoiceService} from '../shared/services/voice/voice.service';
import {TotalSearch, TotalService} from '../shared/services/total/total.service';
import {FooterComponent} from '../shared/components/footer/footer.component';
import {VideoBookmarkService} from '../shared/services/video/video-bookmark.service';
import {OptionService} from "../shared/services/option/option.service";
import {ScrollDownService} from "../shared/services/scroll-down/scroll-down.service";

const EXAMPLE_SELECT_ID = 'total_example_id_';

function totalExampleSelectId(example: Example): string {
  return EXAMPLE_SELECT_ID + example.id;
}

@Component({
  selector: 'app-total',
  templateUrl: './total.component.html',
  styleUrls: ['./total.component.scss'],
  animations: [ triggerDetailPopup ],
})
export class TotalComponent implements OnInit, OnDestroy {
  @ViewChild('container') container: ElementRef;
  @ViewChild(FooterComponent) footer: FooterComponent;
  @ViewChildren(ExampleListItemComponent) listExampleItems: QueryList<ExampleListItemComponent>;

  protected readonly wordSelectId = wordSelectId;
  protected readonly dialectSelectId = dialectSelectId;
  protected readonly proverbSelectedId = proverbSelectedId;
  protected readonly saseungSelectId = saseungSelectId;
  protected readonly totalExampleSelectId = totalExampleSelectId;
  readonly HISTORY_KEY_SEARCH_TOTAL = HISTORY_KEY_SEARCH_TOTAL;


  // 라우터에서 넣어주는 값. (TODO: 초기화에 유의해야 함)
  private queryParams: SearchParam = null;
  private queryParams$: Subscription = null;
  private routeParams$: Subscription = null;

  // 푸터의 아래로 내리기 버튼 구독
  private scrollDown$: Subscription | null = null;

  // 해더에 넣는 값
  public keyword: string = null;

  public totalSearch$: Subscription = null;
  public totalSearch: TotalSearch;
  get countWord(): number {
    return (this.totalSearch && this.totalSearch.countWord) ?? null;
  }
  get listWord(): Array<SimpleWord> {
    return (this.totalSearch && this.totalSearch.listWord) ?? null;
  }
  get countExample(): number {
    return this.totalSearch?.countExample ?? null;
  }
  get listExample(): Array<Example> {
    return this.totalSearch?.listExample ?? null;
  }
  get countDialect(): number {
    return (this.totalSearch && this.totalSearch.countDialect) ?? null;
  }
  get listDialect(): Array<SimpleDialect> {
    return (this.totalSearch && this.totalSearch.listDialect) ?? null;
  }
  get countProverb(): number {
    return (this.totalSearch && this.totalSearch.countProverb) ?? null;
  }
  get listProverb(): Array<SimpleProverb> {
    return (this.totalSearch && this.totalSearch.listProverb) ?? null;
  }
  get countVideo(): number {
    return (this.totalSearch && this.totalSearch.countVideo) ?? null;
  }
  get listVideo(): Array<Video> {
    return (this.totalSearch && this.totalSearch.listVideo) ?? null;
  }
  get countSaseung(): number {
    return this.totalSearch?.countSaseung ?? null;
  }
  get listSaseung(): Array<Saseung3> {
    return this.totalSearch?.listSaseung ?? null;
  }

  // 선택 공통
  public curDetail: SimpleWord | SimpleProverb | SimpleDialect | Example | Saseung3 = null;

  // 상세 팝업 표시 플래그 (속담/사 성 옥편은 curDetail 타입으로 상태 판단)
  public showWordDetail = false;
  public showExampleDetail = false;
  public showDialectDetail = false;
  private oldScrollTop: number;
  public detailHeight: number = 0;         // 상세 팝업창 세로 크기
  private get deltaHeight() {
    // return this.option.largeMode ? WORD_DETAIL_HEIGHT_DELTA_LARGE : WORD_DETAIL_HEIGHT_DELTA;
    return WORD_DETAIL_HEIGHT_DELTA;
  }

  // 사투리 지도 팝업창 관련
  public showOtherDialect = false;
  public mapWord: SimpleWord | null = null; // 사투리 지도에 표시하는 단어
  public mapDialects: Array<SimpleDialect> | null = null; // 사투리 지도에 표시하는 사투리 목록

  // 화면 크기 변경
  private resize$: Subscription = null;

  protected sendingBookmark = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private voice: VoiceService,
    private option: OptionService,
    private totalSvc: TotalService,
    private videoBmSvc: VideoBookmarkService,
    private resizeSvc: ResizeService,
    private scrollDownSvc: ScrollDownService,
    // private naviSvc: NavigationService,
    public lm: LayoutMgrService,
  ) {
    // 이 페이지에 있는데, router.navigate()를 호출하면 이 코드가 다시 불려진다.
    // (주의) 처음 초기화 할 때에도 불려진다.
    this.queryParams$ = this.route.queryParams
      .subscribe(params => {
        console.log('queryParams =', params);
        const oldParams = {...this.queryParams};
        this.queryParams = NormalizeSearchParamKeyword({...params});

        let jasoIdx: JASO | null;  // do not use
        let param: SearchParam;
        [param, jasoIdx, this.keyword] = GetSearchFromQuery(this.queryParams);

        // 상세 팝업창 처리
        if ('wid' in this.queryParams && this.totalSearch) {
          // 단어 상세 팝업창 처리
          const processWordParams = () => {
            const word = this.getWordById(this.queryParams.wid);
            console.log('queryParams:: word=', word);
            if (word) {
              this.CloseAllDetails();
              setTimeout(() => {
                this.DoOpenWordDetail(word);
              }, DETAIL_POPUP_DELAY);
            }
          };

          if (this.totalSearch$ && !this.totalSearch$.closed) {
            // 아직 검색 결과 로딩 중일 수 있음
            this.totalSearch$.add(() => {
              setTimeout(processWordParams, DETAIL_POPUP_DELAY);
            });
          } else {
            processWordParams();
          }
        }
        else if ('did' in this.queryParams && this.totalSearch) {
          // 사투리 상세 팝업창 처리
          const processDialectParams = () => {
            const dialect = this.getDialectById(this.queryParams.did);
            console.log('queryParams:: dialect=', dialect);
            if (dialect) {
              this.CloseAllDetails();
              setTimeout(() => {
                this.DoOpenDialectDetail(dialect);
              }, DETAIL_POPUP_DELAY);
            }
          };

          if (this.totalSearch$ && !this.totalSearch$.closed) {
            // 아직 검색 결과 로딩 중일 수 있음
            this.totalSearch$.add(() => {
              setTimeout(processDialectParams, DETAIL_POPUP_DELAY);
            });
          } else {
            processDialectParams();
          }
        }
        else if ('eid' in this.queryParams && this.totalSearch) {
          // 문장 듣기 상세 팝업창 처리
          const processExampleParams = () => {
            const example = this.getExampleById(this.queryParams.eid);
            console.log('queryParams:: example=', example);
            if (example) {
              this.CloseAllDetails();
              setTimeout(() => {
                this.DoOpenExampleDetail(example);
              }, DETAIL_POPUP_DELAY);
            }
          };

          if (this.totalSearch$ && !this.totalSearch$.closed) {
            // 아직 검색 결과 로딩 중일 수 있음
            this.totalSearch$.add(() => {
              setTimeout(processExampleParams, DETAIL_POPUP_DELAY);
            });
          } else {
            processExampleParams();
          }
        }
        else if ('pid' in this.queryParams && this.totalSearch) {
          // 속담 상세 팝업창 처리
          const processProverbParams = () => {
            const proverb = this.getProverbById(this.queryParams.pid);
            console.log('queryParams:: proverb=', proverb);
            if (proverb) {
              this.CloseAllDetails();
              setTimeout(() => {
                this.DoUnfold(proverb);
              }, DETAIL_POPUP_DELAY);
            }
          };

          if (this.totalSearch$ && !this.totalSearch$.closed) {
            // 아직 검색 결과 로딩 중일 수 있음
            this.totalSearch$.add(() => {
              setTimeout(processProverbParams, DETAIL_POPUP_DELAY);
            });
          } else {
            processProverbParams();
          }
        }
        else if ('sid' in this.queryParams && this.totalSearch) {
          // 사 성 옥편 선택 처리
          const processSaseungParams = () => {
            const saseung = this.getSaseungById(this.queryParams.sid);
            console.log('queryParams:: saseung=', saseung);
            if (saseung) {
              this.CloseAllDetails();
              setTimeout(() => {
                this.DoOpenSaseung(saseung);
              }, DETAIL_POPUP_DELAY);
            }
          };

          if (this.totalSearch$ && !this.totalSearch$.closed) {
            // 아직 검색 결과 로딩 중일 수 있음
            this.totalSearch$.add(() => {
              setTimeout(processSaseungParams, DETAIL_POPUP_DELAY);
            });
          } else {
            processSaseungParams();
          }
        }
        else {
          // wid, did, eid, pid, sid가 없는 경우
          if (('wid' in oldParams && !('wid' in this.queryParams)) ||
              ('did' in oldParams && !('did' in this.queryParams)) ||
              ('eid' in oldParams && !('eid' in this.queryParams)) ||
              ('pid' in oldParams && !('pid' in this.queryParams)) ||
              ('sid' in oldParams && !('sid' in this.queryParams))) {
            // 기존에 wid, did, eid, pid, sid가 있었는데 없어진 경우
            this.closeAllPopup();
          }

          // 검색어가 변경되었거나 아직 검색 결과가 없을 때만 새로 검색
          const oldKeyword = 'keyword' in oldParams ? oldParams.keyword : null;
          const newKeyword = 'keyword' in param ? param.keyword : null;
          if (oldKeyword !== newKeyword || !this.totalSearch) {
            this.ReloadList(param);
          } else if (!IsEqualObj1(param, oldParams)) {
            // 검색어는 같지만 다른 파라미터가 변경된 경우
            console.log('검색어는 동일하나 다른 파라미터 변경됨. 기존 검색 결과 유지');
          }
        }
      });

    // 처음 이 페이지에 들어올 때 불려진다.
    // url이 변경되어도 불린다. type이 변경되어도 주소가 바뀐 것이므로 이 함수가 불린다.
    // queryParams가 바뀐 것으로는 여기가 불려지지 않는다.
    this.routeParams$ = route.params.subscribe(params => {
      console.log('TotalComponent::params = ', params);
    });

    lm.Reset();
  }

  ngOnInit(): void {
    this.resize$ = this.resizeSvc.screenHeight$.subscribe(height => {
      console.log('resize::height = ', height);
      if (this.showWordDetail) {
        const elContainer = this.container.nativeElement;
        const el = this.document.getElementById(wordSelectId(this.curDetail as SimpleWord));
        this.detailHeight = getDetailHeight(elContainer, el, this.footer.height + this.detailHeight);
      }
      else if (this.showDialectDetail) {
        const elContainer = this.container.nativeElement;
        const el = this.document.getElementById(dialectSelectId(this.curDetail as SimpleDialect));
        this.detailHeight = getDetailHeight(elContainer, el, this.footer.height + this.detailHeight);
      }
      else if (this.showExampleDetail) {
        const elContainer = this.container.nativeElement;
        const el = this.document.getElementById(totalExampleSelectId(this.curDetail as Example));
        this.detailHeight = getDetailHeight(elContainer, el, this.footer.height + this.detailHeight);
      }
    });

    // 푸터의 스크롤 버튼 처리.
    this.scrollDown$ = this.scrollDownSvc.pushScrollDown$().subscribe(isFirst => {
      if (isFirst && this.curDetail) {
        if (this.curDetail instanceof SimpleWord) {
          this.CloseWordDetail();
        }
        else if (this.curDetail instanceof SimpleDialect) {
          this.CloseDialectDetail();
        }
        else if (this.curDetail instanceof Example) {
          this.CloseExampleDetail();
        }
        else if (this.curDetail instanceof Saseung3) {
          this.CloseSaseung();
        }
      }
      const scrollDelta = DEFAULT_SCROLL_SPEED * (isFirst ? 2 : 1);  // 스크롤 시작할 때 첫번째는 두배로 스크롤 함.

      this.container?.nativeElement.scrollTo({
        top: this.container.nativeElement.scrollTop + scrollDelta,
        behavior: 'auto',
      });
    });
  }

  ngOnDestroy(): void {
    if (this.resize$) {
      this.resize$.unsubscribe();
      this.resize$ = null;
    }
    if (this.totalSearch$) {
      this.totalSearch$.unsubscribe();
      this.totalSearch$ = null;
    }
    if (this.queryParams$) {
      this.queryParams$.unsubscribe();
      this.queryParams$ = null;
    }
    if (this.routeParams$) {
      this.routeParams$.unsubscribe();
      this.routeParams$ = null;
    }

    this.scrollDownSvc.popScrollDown$();
    if (this.scrollDown$) {
      this.scrollDown$.unsubscribe();
      this.scrollDown$ = null;
    }
    this.closeAllPopup();

    this.clearExampleSeungjo();
  }

  public onScroll($event): void {
    this.clearExampleSeungjo();
  }

  private clearExampleSeungjo(): void {
    // `?.` 는 ngAfterViewInit 이전 초기 렌더 방어용. 통상 경로에서는 항상 populated 됨.
    this.listExampleItems?.forEach(item => item.ClearSeungjo());
  }

  private closeAllPopup(): void {
    this.CloseAllDetails();
    if (this.showOtherDialect) {
      this.CloseDialectMap();
    }
  }

  private CloseAllDetails(): void {
    if (this.showWordDetail) {
      this.DoCloseWordDetail();
    }
    else if (this.showDialectDetail) {
      this.DoCloseDialectDetail();
    }
    else if (this.showExampleDetail) {
      this.DoCloseExampleDetail();
    }
    else if (this.IsProverbSelected(this.curDetail as SimpleProverb)) {
      this.DoCloseProverb();
    }
    else if (this.IsSaseungSelected(this.curDetail as Saseung3)) {
      this.DoCloseSaseung();
    }
  }

  /****************************************************************************
   * 목록 로딩 처리.
   ****************************************************************************/
  public onSearch(param: SearchParam): void {
    console.log('TotalComponent::onSearch() = ', param);

    this.router.navigate(['/total'], {
      queryParams: NormalizeSearchParamKeyword(param),
    }).then();

    this.closeAllPopup();
  }

  public ReloadList(param: SearchParam): void {
    param = NormalizeSearchParamKeyword(param);
    console.log('TotalComponent::ReloadList() = ', param);
    if ('keyword' in param) {
      // 새로운 키워드 검색
      this.totalSearch$ = this.totalSvc.GetTotalSearch(param.keyword, this.option.wordLevel).pipe(
        finalize(() => this.totalSearch$ = null)
      ).subscribe(
        data => {
          console.log(data);
          this.totalSearch = data;

          // 검색 결과가 로드된 후 상세 항목 처리
          if (this.queryParams && 'wid' in this.queryParams) {
            const word = this.getWordById(this.queryParams.wid);
            if (word) {
              this.DoOpenWordDetail(word);
            }
          } else if (this.queryParams && 'did' in this.queryParams) {
            const dialect = this.getDialectById(this.queryParams.did);
            if (dialect) {
              this.DoOpenDialectDetail(dialect);
            }
          } else if (this.queryParams && 'eid' in this.queryParams) {
            const example = this.getExampleById(this.queryParams.eid);
            if (example) {
              this.DoOpenExampleDetail(example);
            }
          } else if (this.queryParams && 'pid' in this.queryParams) {
            const proverb = this.getProverbById(this.queryParams.pid);
            if (proverb) {
              this.DoUnfold(proverb);
            }
          } else if (this.queryParams && 'sid' in this.queryParams) {
            const saseung = this.getSaseungById(this.queryParams.sid);
            if (saseung) {
              this.DoOpenSaseung(saseung);
            }
          }
        },
        (err: HttpErrorResponse) => {
          console.error(err);
          if (err.status === 404) {
            this.totalSearch = null;
          }
          else {
            // TODO: 에러 출력
          }
        }
      );
    }
    else {
      console.error('TotalComponent::ReloadList() param does not contain keyword', param);
    }
  }

  /****************************************************************************
   * 단어 상세 팝업창 처리 코드
   ***************************************************************************/

  /**
   * 단어 상세 보기 라우터 처리
   */
  public OpenWordDetail($event: MouseEvent, word: SimpleWord): void {
    $event.preventDefault();
    $event.stopPropagation();

    if (this.showWordDetail) {
      console.warn('OpenWordDetail() already opened!');
      return;
    }

    const queryParams = {
      ...this.queryParams,
      wid: word.id,
    };
    delete queryParams.did; // 다른 상세 페이지 ID 제거
    delete queryParams.eid; // 다른 상세 페이지 ID 제거
    delete queryParams.pid; // 다른 상세 페이지 ID 제거
    delete queryParams.sid; // 다른 상세 페이지 ID 제거

    this.router.navigate(['/total'], {queryParams}).then();
  }

  /**
   * 실제 단어 상세 보기 UI 처리
   */
  private DoOpenWordDetail(word: SimpleWord): void {
    if (this.showWordDetail) {
      console.warn('DoOpenWordDetail() already opened!');
      return;
    }

    this.showWordDetail = true;
    this.curDetail = word;

    timer(CONTAINER_SCROLL_DELAY).subscribe(() => {
      const el = this.document.getElementById(wordSelectId(word));
      if (!el) {
        console.error(`TotalComponent::document.getElementById(${wordSelectId(word)}) is null`);
        return;
      }

      const elContainer = this.container.nativeElement;
      const delta = this.deltaHeight;
      this.detailHeight = getDetailHeight(elContainer, el, this.footer.height + delta);
      this.oldScrollTop = scrollToNode(elContainer, el, -delta);
    });
  }

  /**
   * 상세 단어장 닫기 라우터 처리
   */
  public CloseWordDetail(bookmark: boolean = null): void {
    console.log('CloseWordDetail()', bookmark);

    const queryParams = {
      ...this.queryParams,
    };
    delete queryParams.wid;

    this.router.navigate(['/total'], {queryParams}).then();
  }

  /**
   * 실제 단어 상세 닫기 UI 처리
   */
  private DoCloseWordDetail(): void {
    // 선택 해제 시 원래 위치로 되돌리는 복구 메커니즘이 있었지만 현재는 비활성화했다.
    // this.container.nativeElement.scrollTo({ top: this.oldScrollTop, behavior: 'smooth' });

    this.voice.Stop();
    this.showWordDetail = false;

    if (this.curDetail) {
      this.curDetail = null;
    }

    if (this.lm.showHelp) {
      // 만약 단어 도움말 창이 열려있을경우
      this.CloseWordHelp();
    }
  }

  /****************************************************************************
   * 단어 도움말 팝업창 처리 코드
   ***************************************************************************/

  public OpenWordHelp(): void {
    this.voice.Stop();
    this.lm.showHelp = true;
  }

  public CloseWordHelp(showSaseung: boolean|null = null): void {
    this.lm.showHelp = false;

    if (showSaseung !== null) {
      this.option.showSaseung = showSaseung;
    }
  }

  public ClosePitchHelp(): void {
    this.lm.showPitchHelp = false;
  }

  /****************************************************************************
   * 문장 듣기 상세 팝업창 처리 코드
   ***************************************************************************/

  /**
   * 문장 듣기 상세 보기 라우터 처리
   */
  public OpenExampleDetail($event: MouseEvent, example: Example): void {
    $event.preventDefault();
    $event.stopPropagation();

    if (this.showExampleDetail) {
      console.warn('OpenExampleDetail() already opened!');
      return;
    }

    const queryParams = {
      ...this.queryParams,
      eid: example.id,
    };
    delete queryParams.wid; // 다른 상세 페이지 ID 제거
    delete queryParams.did; // 다른 상세 페이지 ID 제거
    delete queryParams.pid; // 다른 상세 페이지 ID 제거
    delete queryParams.sid; // 다른 상세 페이지 ID 제거

    this.router.navigate(['/total'], {queryParams}).then();
  }

  /**
   * 실제 문장 듣기 상세 보기 UI 처리
   */
  private DoOpenExampleDetail(example: Example): void {
    if (this.showExampleDetail) {
      console.warn('DoOpenExampleDetail() already opened!');
      return;
    }

    this.showExampleDetail = true;
    this.curDetail = example;

    timer(CONTAINER_SCROLL_DELAY).subscribe(() => {
      const el = this.document.getElementById(totalExampleSelectId(example));
      if (!el) {
        console.error(`TotalComponent::document.getElementById(${totalExampleSelectId(example)}) is null`);
        return;
      }

      const elContainer = this.container.nativeElement;
      const delta = EXAMPLE_DETAIL_HEIGHT_DELTA;
      this.detailHeight = getDetailHeight(elContainer, el, this.footer.height + delta);
      this.oldScrollTop = scrollToNode(elContainer, el, -delta);
    });
  }

  /**
   * 문장 듣기 상세 닫기 라우터 처리
   */
  public CloseExampleDetail(): void {
    console.log('CloseExampleDetail()');

    const queryParams = {
      ...this.queryParams,
    };
    delete queryParams.eid;

    this.router.navigate(['/total'], {queryParams}).then();
  }

  /**
   * 실제 문장 듣기 상세 닫기 UI 처리
   */
  private DoCloseExampleDetail(): void {
    // 선택 해제 시 원래 위치로 되돌리는 복구 메커니즘이 있었지만 현재는 비활성화했다.
    // this.container.nativeElement.scrollTo({ top: this.oldScrollTop, behavior: 'smooth' });

    this.voice.Stop();
    this.showExampleDetail = false;

    if (this.curDetail) {
      this.curDetail = null;
    }

    if (this.lm.showExampleHelp) {
      this.CloseExampleHelp();
    }
  }

  public OpenExampleHelp(): void {
    this.voice.Stop();
    this.lm.showExampleHelp = true;
  }

  public CloseExampleHelp(): void {
    this.lm.showExampleHelp = false;
  }

  /****************************************************************************
   * 사투리 상세 팝업창 처리 코드
   ***************************************************************************/
  /**
   * 사투리 상세 보기 라우터 처리
   */
  public OpenDialectDetail($event: Event, dialect: SimpleDialect): void {
    $event.preventDefault();
    $event.stopPropagation();

    if (this.showDialectDetail) {
      console.warn('OpenDialectDetail() already opened!');
      return;
    }

    const queryParams = {
      ...this.queryParams,
      did: dialect.id,
    };
    delete queryParams.wid; // 다른 상세 페이지 ID 제거
    delete queryParams.eid; // 다른 상세 페이지 ID 제거
    delete queryParams.pid; // 다른 상세 페이지 ID 제거
    delete queryParams.sid; // 다른 상세 페이지 ID 제거

    this.router.navigate(['/total'], {queryParams}).then();
  }

  /**
   * 실제 사투리 상세 보기 UI 처리
   */
  private DoOpenDialectDetail(dialect: SimpleDialect): void {
    if (this.showDialectDetail) {
      console.warn('DoOpenDialectDetail() already opened!');
      return;
    }

    this.showDialectDetail = true;
    this.curDetail = dialect;

    timer(CONTAINER_SCROLL_DELAY).subscribe(() => {
      const el = this.document.getElementById(dialectSelectId(dialect));
      if (!el) {
        console.error(`TotalComponent::document.getElementById(${dialectSelectId(dialect)}) is null`);
        return;
      }

      const elContainer = this.container.nativeElement;
      const delta = DIALECT_DETAIL_HEIGHT_DELTA;
      this.detailHeight = getDetailHeight(elContainer, el, this.footer.height + DIALECT_TOTAL_HEIGHT_DELTA);
      this.oldScrollTop = scrollToNode(elContainer, el, -delta);
    });
  }

  /**
   * 사투리 상세 닫기 라우터 처리
   */
  public CloseDialectDetail(bookmark: boolean = null): void {
    console.log('CloseDialectDetail()', bookmark);

    const queryParams = {
      ...this.queryParams,
    };
    delete queryParams.did;

    this.router.navigate(['/total'], {queryParams}).then();
  }

  /**
   * 실제 사투리 상세 닫기 UI 처리
   */
  private DoCloseDialectDetail(): void {
    // 선택 해제 시 원래 위치로 되돌리는 복구 메커니즘이 있었지만 현재는 비활성화했다.
    // this.container.nativeElement.scrollTo({ top: this.oldScrollTop, behavior: 'smooth' });

    this.showDialectDetail = false;

    if (this.curDetail) {
      this.curDetail = null;
    }
  }

  /****************************************************************************
   * 사투리 지도 팝업 관련
   ***************************************************************************/

  public OpenDialectMapWord(word: Word): void {
    if (this.showOtherDialect) {
      console.warn('사투리 지도 팝업창이 이미 열려있음!');
      return;
    }
    this.showOtherDialect = true;
    this.mapWord = word;
    this.mapDialects = word.dialects;
  }

  public OpenDialectMapDialect(mapPayload: DialectMapPayload): void {
    if (this.showOtherDialect) {
      console.warn('사투리 지도 팝업창이 이미 열려있음!');
      return;
    }
    this.showOtherDialect = true;
    this.mapWord = mapPayload.word;
    this.mapDialects = mapPayload.dialects;
  }

  public CloseDialectMap(): void {
    if (!this.showOtherDialect) {
      console.warn('사투리 지도 팝업창이 이미 닫혀있음!');
      return;
    }
    this.showOtherDialect = false;
    this.mapWord = null;
    this.mapDialects = null;
  }

  /****************************************************************************
   * 속담 선택 시 처리 코드
   ***************************************************************************/

  public IsProverbSelected(proverb: SimpleProverb): boolean {
    return this.curDetail && this.curDetail instanceof SimpleProverb && this.curDetail.id === proverb.id;
  }

  /**
   * 속담 상세 보기 라우터 처리
   */
  public Unfold($event: MouseEvent, proverb: SimpleProverb): void {
    console.log('Unfold: ', proverb);
    $event.preventDefault();
    $event.stopPropagation();

    if (this.IsProverbSelected(proverb)) {
      console.warn('Unfold() already opened!');
      return;
    }

    const queryParams = {
      ...this.queryParams,
      pid: proverb.id,
    };
    delete queryParams.wid; // 다른 상세 페이지 ID 제거
    delete queryParams.did; // 다른 상세 페이지 ID 제거
    delete queryParams.eid; // 다른 상세 페이지 ID 제거
    delete queryParams.sid; // 다른 상세 페이지 ID 제거

    this.router.navigate(['/total'], {queryParams}).then();
  }

  /**
   * 실제 속담 상세 보기 UI 처리
   */
  private DoUnfold(proverb: SimpleProverb): void {
    console.log('DoUnfold: ', proverb);

    if (this.IsProverbSelected(proverb)) {
      console.warn('DoUnfold() already opened!');
      return;
    }

    this.showWordDetail = false;
    this.showDialectDetail = false;
    this.curDetail = proverb;

    const el = this.document.getElementById(proverbSelectedId(proverb));
    if (!el) {
      console.error(`TotalComponent::document.getElementById(${proverbSelectedId(proverb)}) is null`);
      return;
    }

    const elContainer = this.container.nativeElement;
    consoleEl(el as HTMLElement);
    consoleEl(elContainer);

    timer(CONTAINER_SCROLL_DELAY).subscribe(() => {
      this.oldScrollTop = scrollToNode(elContainer, el, - this.deltaHeight);
    });
  }

  /**
   * 속담 닫기 라우터 처리
   */
  public CloseProverb(): void {
    console.log('CloseProverb()');

    const queryParams = {
      ...this.queryParams,
    };
    delete queryParams.pid;

    this.router.navigate(['/total'], {queryParams}).then();
  }

  /**
   * 실제 속담 닫기 UI 처리
   */
  private DoCloseProverb(): void {
    console.log('DoCloseProverb()');
    // 선택 해제 시 원래 위치로 되돌리는 복구 메커니즘이 있었지만 현재는 비활성화했다.
    // this.container.nativeElement.scrollTo({ top: this.oldScrollTop, behavior: 'smooth' });

    if (this.curDetail) {
      this.curDetail = null;
    }
  }

  /****************************************************************************
   * 사 성 옥편 선택 시 처리 코드
   ***************************************************************************/

  public IsSaseungSelected(saseung: Saseung3): boolean {
    return this.curDetail && this.curDetail instanceof Saseung3 && this.curDetail.id === saseung.id;
  }

  /**
   * 사 성 옥편 선택 라우터 처리
   */
  public OpenSaseung($event: MouseEvent, saseung: Saseung3): void {
    console.log('OpenSaseung: ', saseung);
    $event.preventDefault();
    $event.stopPropagation();

    if (this.IsSaseungSelected(saseung)) {
      console.warn('OpenSaseung() already opened!');
      return;
    }

    const queryParams = {
      ...this.queryParams,
      sid: saseung.id,
    };
    delete queryParams.wid; // 다른 상세 페이지 ID 제거
    delete queryParams.did; // 다른 상세 페이지 ID 제거
    delete queryParams.eid; // 다른 상세 페이지 ID 제거
    delete queryParams.pid; // 다른 상세 페이지 ID 제거

    this.router.navigate(['/total'], {queryParams}).then();
  }

  /**
   * 실제 사 성 옥편 선택 UI 처리
   */
  private DoOpenSaseung(saseung: Saseung3): void {
    console.log('DoOpenSaseung: ', saseung);

    if (this.IsSaseungSelected(saseung)) {
      console.warn('DoOpenSaseung() already opened!');
      return;
    }

    this.showWordDetail = false;
    this.showDialectDetail = false;
    this.showExampleDetail = false;
    this.curDetail = saseung;

    const el = this.document.getElementById(saseungSelectId(saseung));
    if (!el) {
      console.error(`TotalComponent::document.getElementById(${saseungSelectId(saseung)}) is null`);
      this.curDetail = null;
      return;
    }

    const elContainer = this.container.nativeElement;
    consoleEl(el as HTMLElement);
    consoleEl(elContainer);

    timer(CONTAINER_SCROLL_DELAY).subscribe(() => {
      this.oldScrollTop = scrollToNode(elContainer, el, - this.deltaHeight);
    });
  }

  /**
   * 사 성 옥편 선택 닫기 라우터 처리
   */
  public CloseSaseung(): void {
    console.log('CloseSaseung()');

    const queryParams = {
      ...this.queryParams,
    };
    delete queryParams.sid;

    this.router.navigate(['/total'], {queryParams}).then();
  }

  /**
   * 실제 사 성 옥편 선택 닫기 UI 처리
   */
  private DoCloseSaseung(): void {
    console.log('DoCloseSaseung()');
    // 선택 해제 시 원래 위치로 되돌리는 복구 메커니즘이 있었지만 현재는 비활성화했다.
    // this.container.nativeElement.scrollTo({ top: this.oldScrollTop, behavior: 'smooth' });

    if (this.curDetail) {
      this.curDetail = null;
    }
  }

  public OnSaseungBookmarkChange(saseung: Saseung3, bookmark: boolean): void {
    saseung.bookmark = bookmark;
  }

  /****************************************************************************
   * 동영상 관련 처리
   ***************************************************************************/

  public VideoToggleBookmark(video: Video): void {
    if (this.sendingBookmark) {
      return;
    }
    this.sendingBookmark = true;

    video.bookmark = !video.bookmark;
    this.videoBmSvc.sendBookmark(video.no, video.bookmark).pipe(
      finalize(() => this.sendingBookmark = false)
    ).subscribe(
      data => console.log(data),
      err => console.error(err)
    );
  }

  /****************************************************************************
   * 항목별 ID 조회 헬퍼 함수 (단어/사투리/문장 듣기/속담/사 성 옥편)
   ****************************************************************************/
  private getWordById(id: string): SimpleWord | null {
    if (!this.listWord) return null;
    return this.listWord.find(word => word.id === id) || null;
  }

  private getDialectById(id: string): SimpleDialect | null {
    if (!this.listDialect) return null;
    return this.listDialect.find(dialect => dialect.id === id) || null;
  }

  private getExampleById(id: string): Example | null {
    if (!this.listExample) return null;
    return this.listExample.find(example => example.id === id) || null;
  }

  private getProverbById(id: string): SimpleProverb | null {
    if (!this.listProverb) return null;
    return this.listProverb.find(proverb => proverb.id === id) || null;
  }

  private getSaseungById(id: string): Saseung3 | null {
    if (!this.listSaseung) return null;
    return this.listSaseung.find(saseung => saseung.id === id) || null;
  }
}
